<template>
  <v-dialog v-model="visible" :max-width="isMobile ? '100%' : '900'" content-class="material-selector-dialog">
    <v-card class="bg-neutral-900 !rounded-2xl flex flex-col" :style="{ height: isMobile ? '420px' : '85vh', maxHeight: isMobile ? '420px' : '85vh' }">
      <!-- Fixed header -->
      <v-card-title class="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-neutral-800">
        <span class="text-xl font-bold text-white">Materials History</span>
        <v-btn icon @click="visible = false" variant="text" density="compact">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <!-- Scrollable content area -->
      <v-card-text class="flex-1 overflow-hidden px-0 py-0" style="min-height: 0;">
        <div class="h-full overflow-y-auto overflow-x-hidden px-4 md:px-6 py-4" ref="scrollContainer" @scroll="handleScroll" :style="isMobile ? '-webkit-overflow-scrolling: touch;' : 'scrollbar-width: thin; scrollbar-color: #666 #333;'">
        <div v-if="loading && materials.length === 0" class="flex justify-center items-center h-60">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        
        <div v-else-if="displayableMaterials.length === 0 && !loading" class="flex flex-col justify-center items-center h-60">
          <img src="/assets/images/empty.png" class="w-32 h-32" alt="No materials">
          <p class="mt-4 text-gray-400">No valid materials available</p>
        </div>
        
        <div v-else>
          <div :class="isMobile ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-4 md:grid-cols-5 gap-2 md:gap-3'">
            <div 
              v-for="material in displayableMaterials" 
              :key="material.material_id"
              class="relative group cursor-pointer"
              @click="selectMaterial(material)"
            >
              <div class="relative w-full aspect-[4/3] rounded-md overflow-hidden bg-neutral-800">
                <img
                  v-if="material.cover_url || !isVideoFormat(material)"
                  :src="material.cover_url || material.convert_url || material.url"
                  class="w-full h-full object-cover"
                  :alt="material.material_id"
                >
                <video
                  v-else
                  :src="`${material.convert_url || material.url}#t=0.1`"
                  class="w-full h-full object-cover"
                  muted
                  playsinline
                  preload="metadata"
                />
                
                <!-- Video indicator -->
                <div v-if="isVideoFormat(material) && material.duration" :class="isMobile ? 'absolute bottom-1 left-1' : 'absolute bottom-2 left-2'">
                  <div class="bg-black bg-opacity-70 px-1 py-0.5 rounded text-xs text-white">
                    {{ formatDuration(material.duration) }}
                  </div>
                </div>
                
                <!-- Delete button -->
                <div v-if="!isDemo(material)" 
                     class="w-7 h-7 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 duration-200 hover:bg-red-600 cursor-pointer absolute top-1 right-[0.1rem]"
                     @click.stop="confirmDelete(material)">
                  <v-icon size="16" color="white">mdi-delete-outline</v-icon>
                </div>
                
                <!-- Selection checkbox -->
                <div 
                     class="w-6 h-6 absolute bottom-1 right-1">
                  <div v-if="selectedMaterial?.material_id === material.material_id" 
                       class="w-full h-full bg-orange-500 rounded-lg flex items-center justify-center">
                    <v-icon size="16" color="white">mdi-check</v-icon>
                  </div>
                  <div v-else class="w-full h-full border-2 border-white border-opacity-50 rounded-lg bg-transparent"></div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Loading more indicator with fixed height -->
          <div v-if="loadingMore" class="mt-4 h-12 flex justify-center items-center">
            <v-progress-circular indeterminate color="primary" size="small"></v-progress-circular>
          </div>
          
          <!-- Manual load more button (show when we have more but auto-load might not trigger) -->
          <div v-else-if="hasMore" class="mt-4 h-12 flex justify-center items-center">
            <v-btn @click="loadMaterials(true)" variant="text" color="primary" size="small">
              Load More
            </v-btn>
          </div>
          
          <!-- End of list indicator -->
          <div v-else-if="displayableMaterials.length > 0" class="mt-4 h-12 flex justify-center items-center">
            <span class="text-gray-500 text-sm">No more materials</span>
          </div>
          
          <!-- Spacer to ensure scrollability when content is borderline -->
          <div v-if="hasMore && !loadingMore" :class="isMobile ? 'h-10' : 'h-20'"></div>
        </div>
        </div>
      </v-card-text>
      
      <!-- Fixed footer -->
      <v-card-actions class="flex-shrink-0 px-6 py-2 border-t border-neutral-800 flex justify-center">
        <v-btn 
          size="large"
          :disabled="!selectedMaterial"
          @click="confirmSelection"
          class="!bg-orange-600 !text-black font-bold !h-12 !px-8 !rounded-full"
        >
          Try This Material
        </v-btn>
      </v-card-actions>
    </v-card>
    
    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card class="bg-neutral-800 !rounded-2xl">
        <v-card-title class="text-center pt-6">
          <span class="text-xl font-bold text-white">Confirm Deletion</span>
        </v-card-title>
        <v-card-text class="text-center pb-2">
          <p class="text-gray-300">Are you sure you want to delete the history of uploaded materials?</p>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 flex gap-3">
          <v-btn 
            color="primary" 
            variant="flat"
            size="large"
            @click="deleteDialog = false"
            class="flex-1 !bg-orange-600 !text-black font-semibold !rounded-full"
          >
            Cancel
          </v-btn>
          <v-btn 
            variant="flat"
            size="large"
            @click="deleteMaterial"
            class="flex-1 !bg-orange-900/40 !text-orange-600 font-semibold !rounded-full"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useDisplay } from 'vuetify';
import api from '~/api';
import appConfig from '~/app.config';
import type { Material, MaterialWithStatus } from '~/types';

const props = defineProps<{
  modelValue: boolean;
  /** sex-pose / ai-video 只收图，传 true 时过滤掉视频素材 */
  imageOnly?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'select': [material: MaterialWithStatus];
}>();

const display = useDisplay();
const isMobile = computed(() => display.xs.value);

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const loading = ref(false);
const loadingMore = ref(false);
const materials = ref<MaterialWithStatus[]>([]);
const selectedMaterial = ref<MaterialWithStatus | null>(null);
const currentPage = ref(1);
const pageSize = 10; // Use consistent page size with MaterialsHistory
const totalCount = ref(0);
const deleteDialog = ref(false);
const materialToDelete = ref<MaterialWithStatus | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const hasMore = ref(true);

const snackbar = useSnackbarStore();
const globalLoading = useLoadingStore();

// 使用 useState 来与 /my/uploads 页面保持一致
const matObj = useState<MaterialWithStatus>('matObj', () => null);

// 使用共享的材料数据，避免重复加载
const sharedMaterials = useState<MaterialWithStatus[]>('sharedMaterials', () => []);
const sharedMaterialsTotal = useState<number>('sharedMaterialsTotal', () => 0);
const sharedMaterialsLoaded = useState<boolean>('sharedMaterialsLoaded', () => false);

// Computed property for displayable materials (excluding failed ones)
const displayableMaterials = computed(() => {
  let list = materials.value.filter(m => m.matStat !== 'failed' && m.matStat !== 'prohibit');
  if (props.imageOnly) list = list.filter(m => !isVideoFormat(m));
  return list;
});

const isVideoFormat = (mat: MaterialWithStatus): boolean => {
  // First check if format field exists and use it
  if (mat.format) {
    return appConfig.videoExt.some(format => format.ext === mat.format);
  }
  
  // Fallback to URL extension checking
  const urlExt = (mat.cover_url || mat.convert_url || mat.url)?.split('.').pop()?.toLowerCase();
  return appConfig.videoExt.some(format => format.ext === urlExt);
};

const isDemo = (material: MaterialWithStatus): boolean => {
  // Demo materials don't have material_id or have special demo flag
  return !material.material_id || material.isDemo === true;
};

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const handleMats = (mats: Material[]) => {
  return mats.map(mat => {
    let matStat: 'analyzing' | 'success' | 'failed' | 'prohibit' = 'success';
    switch (mat.detect_status) {
      case 0:
        matStat = 'analyzing';
        break;
      case 1:
        matStat = 'success';
        break;
      case -1:
        matStat = 'failed';
        break;
      case -2:
        matStat = 'prohibit';
        break;
    }
    return { ...mat, matStat };
  });
};

const loadMaterials = async (append = false) => {
  if (!hasMore.value && append) return;
  if (loadingMore.value || loading.value) return;
  
  try {
    // For initial load, check if we have shared data
    if (!append && sharedMaterialsLoaded.value && sharedMaterials.value.length > 0) {
      loading.value = true;
      
      // Use the shared materials for the first batch
      materials.value = sharedMaterials.value;
      totalCount.value = sharedMaterialsTotal.value;
      hasMore.value = sharedMaterials.value.length < sharedMaterialsTotal.value;
      
      // We already have page 1 loaded (10 items)
      currentPage.value = 1;
      
      // Check if we need more content to fill the view
      if (hasMore.value) {
        nextTick(() => {
          checkAndLoadMore();
        });
      }
      
      loading.value = false;
      return;
    }
    
    if (append) {
      loadingMore.value = true;
      // Increment page for append
      currentPage.value++;
    } else {
      loading.value = true;
      materials.value = [];
      currentPage.value = 1;
    }
    
    // Simple pagination: always use pageSize 10
    const resp = await api.getMyUploads('all', currentPage.value, pageSize);
    
    if (resp) {
      // Don't filter - show all materials
      const allMaterials = handleMats(resp.lists || []);
      
      if (append) {
        // Use requestAnimationFrame to ensure smooth rendering
        await nextTick();
        materials.value = [...materials.value, ...allMaterials];
      } else {
        materials.value = allMaterials;
        // Update shared state if this is the first load
        if (currentPage.value === 1 && !sharedMaterialsLoaded.value) {
          sharedMaterials.value = allMaterials;
          sharedMaterialsTotal.value = resp.total;
          sharedMaterialsLoaded.value = true;
        }
      }
      
      totalCount.value = resp.total;
      hasMore.value = materials.value.length < resp.total;
      
      // After loading, check if we need more content to fill the view
      // Check even for append to handle filtered content
      nextTick(() => {
        checkAndLoadMore();
      });
    }
  } catch (error) {
    console.error('Failed to load materials:', error);
    snackbar.showError('Failed to load materials');
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

// Check if scroll container has space and load more if needed
const checkAndLoadMore = () => {
  if (!scrollContainer.value || !hasMore.value || loadingMore.value) return;
  
  const container = scrollContainer.value;
  // Check if we have enough displayable content
  // Consider both the container height and the actual displayed items
  const needsMoreContent = container.scrollHeight <= container.clientHeight + 10;
  
  // Also check if we have very few displayable items (after filtering)
  // This handles the case where many materials are filtered out
  const minDisplayableItems = isMobile.value ? 6 : 15; // Mobile: 2 rows, Desktop: at least 3 rows
  const hasEnoughDisplayable = displayableMaterials.value.length >= minDisplayableItems;
  
  // Load more if:
  // 1. Container is not scrollable yet AND we haven't reached minimum items
  // 2. We have very few displayable items compared to total loaded
  if (needsMoreContent && (!hasEnoughDisplayable || displayableMaterials.value.length < materials.value.length * 0.3)) {
    // Don't increment here, loadMaterials(true) will handle it
    loadMaterials(true);
  }
};

// Handle scroll event for infinite loading
const handleScroll = () => {
  if (!scrollContainer.value || loadingMore.value || !hasMore.value) return;
  
  const container = scrollContainer.value;
  const scrollPosition = container.scrollTop + container.clientHeight;
  const scrollHeight = container.scrollHeight;
  
  // Load more when scrolled to within 150px of bottom
  if (scrollHeight - scrollPosition < 150) {
    loadMaterials(true);
  }
};

const selectMaterial = (material: MaterialWithStatus) => {
  if (material.matStat === 'failed' || material.matStat === 'prohibit') {
    snackbar.showError('This material is invalid and cannot be used');
    return;
  }
  if (material.matStat === 'analyzing') {
    snackbar.showError('This material is still being analyzed, please wait');
    return;
  }
  selectedMaterial.value = material;
};

const confirmSelection = () => {
  if (selectedMaterial.value) {
    // Set the material in useState to maintain consistency with /my/uploads page flow
    matObj.value = selectedMaterial.value;
    
    emit('select', selectedMaterial.value);
    visible.value = false;
  }
};

const confirmDelete = (material: MaterialWithStatus) => {
  materialToDelete.value = material;
  deleteDialog.value = true;
};

const deleteMaterial = async () => {
  if (!materialToDelete.value) return;
  
  try {
    globalLoading.startLoading('Deleting...');
    await api.deleteMat(materialToDelete.value.material_id);
    snackbar.showSnackbar({ color: 'success', message: 'Deleted successfully' });
    
    // Clear shared state to force reload
    sharedMaterialsLoaded.value = false;
    sharedMaterials.value = [];
    
    // Reset pagination
    currentPage.value = 1;
    materials.value = [];
    
    // Reload materials
    await loadMaterials();
    
    // Clear selection if deleted material was selected
    if (selectedMaterial.value?.material_id === materialToDelete.value.material_id) {
      selectedMaterial.value = null;
    }
  } catch (error) {
    snackbar.showError(error.response?.data?.message || 'Failed to delete material');
  } finally {
    globalLoading.stopLoading();
    deleteDialog.value = false;
    materialToDelete.value = null;
  }
};

// Load materials when dialog opens
watch(visible, async (newVal) => {
  if (newVal) {
    currentPage.value = 1;
    selectedMaterial.value = null;
    hasMore.value = true;
    materials.value = [];
    await loadMaterials(false);
    // After initial load, check if we need more
    nextTick(() => {
      checkAndLoadMore();
    });
  }
});
</script>

<style scoped>
.material-selector-dialog :deep(.v-overlay__content) {
  border-radius: 16px !important;
}
.material-selector-dialog :deep(.v-card) {
  display: flex;
  flex-direction: column;
}

.material-selector-dialog :deep(.v-card-text) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

/* Mobile specific styles */
@media (max-width: 600px) {
  .material-selector-dialog :deep(.v-dialog) {
    margin: 24px;
  }
  
  .material-selector-dialog :deep(.v-card) {
    display: flex;
    flex-direction: column;
    height: 420px;
  }
}

/* Custom scrollbar styles for webkit browsers */
.material-selector-dialog :deep(::-webkit-scrollbar) {
  width: 8px;
}

.material-selector-dialog :deep(::-webkit-scrollbar-track) {
  background: #333;
  border-radius: 4px;
}

.material-selector-dialog :deep(::-webkit-scrollbar-thumb) {
  background: #666;
  border-radius: 4px;
}

.material-selector-dialog :deep(::-webkit-scrollbar-thumb:hover) {
  background: #888;
}
</style>